'use client';

import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { PortableTextBlock } from 'sanity';
import { stegaClean } from '@sanity/client/stega';

import { GroqImage } from '@/helpers/Image';
import { GROQButton, ICareerProcessStep } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';

const ease = [0.25, 0.1, 0.25, 1] as const;

export interface ApplicationProcessSectionData {
  processChip?: string;
  processTitle?: PortableTextBlock[];
  processSteps?: ICareerProcessStep[];
}

const TEXTURE_MASK =
  'radial-gradient(142.67rem 54.44rem at 105.12rem -18.86rem, #000 0%, transparent 100%)';

function StepCard({ step, index }: { step: ICareerProcessStep; index: number }) {
  return (
    <div className="relative isolate flex w-full flex-col overflow-hidden rounded-[0.2835rem] bg-[#f4f4f4] max-lg:min-h-[26.375rem] lg:h-[63.03rem]">
      <div
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.3]"
        style={{ maskImage: TEXTURE_MASK, WebkitMaskImage: TEXTURE_MASK }}
        aria-hidden="true"
      >
        <div
          className="absolute left-[-3.764rem] top-[-47.35rem] h-[107.3rem] w-[110.44rem] rotate-90"
          style={{ backgroundImage: 'url(/careers/hero-dots.png)', backgroundSize: '100% 100%' }}
        />
      </div>

      <div className="relative z-10 flex flex-col max-lg:flex-1 max-lg:justify-between max-lg:gap-[1.725rem] max-lg:px-[3.5rem] max-lg:py-[3rem] lg:px-[4.536rem] lg:pt-[3.402rem]">
        <div className="flex h-[6.1rem] w-[6.1rem] shrink-0 rounded-[0.2709rem] p-[0.4064rem] shadow-[inset_0_0_0_0.0677rem_rgba(0,27,20,0.1)]">
          <div className="flex h-full w-full items-center justify-center rounded-[0.2297rem] bg-[#e8e9e9]">
            <span className="relative top-[0.2655rem] font-blacker text-[3.2272rem] italic leading-[1.15] tracking-[-0.05em] text-[#196551]">
              {String(index + 1).padStart(2, '0')}
            </span>
          </div>
        </div>

        <div className="flex flex-col lg:mt-[3.475rem]">
          {step.title && (
            <p className="mb-0 text-[3rem] font-medium leading-[1.06] tracking-[-0.05em] text-[#103037]">
              {step.title}
            </p>
          )}
          {step.description && (
            <p className="mb-0 text-[2.25rem] leading-[1.45] tracking-[-0.05em] text-[rgba(16,48,55,0.72)] max-lg:mt-[1.225rem] lg:mt-[0.725rem] lg:whitespace-pre-line">
              {step.description}
            </p>
          )}
        </div>
      </div>

      {step.image && (
        <div
          className="pointer-events-none relative mt-auto hidden h-[34rem] w-full overflow-hidden lg:block"
          aria-hidden="true"
        >
          <GroqImage
            src={step.image}
            alt=""
            quality={100}
            style={{ width: `${step.imageScale ?? 100}%`, height: 'auto' }}
            className="!absolute !left-1/2 !top-0 !max-w-none !-translate-x-1/2"
          />
        </div>
      )}
    </div>
  );
}

export default function ApplicationProcessSection({
  data,
  cta,
}: {
  data?: ApplicationProcessSectionData;
  cta?: GROQButton;
}) {
  const steps = data?.processSteps ?? [];
  // The shared open-positions CTA targets the overview's job list.
  const processCTA: GROQButton | undefined =
    cta?.cta && stegaClean(cta.cta.action) === 'anchor' && stegaClean(cta.cta.anchor) === '#jobs'
      ? {
          ...cta,
          cta: {
            ...cta.cta,
            action: 'link',
            link: { externalLink: true, url: '/careers#jobs', openInNewTab: false },
          },
        }
      : cta;
  if (!data || (!data.processTitle && steps.length === 0)) return null;

  return (
    <section className="relative w-full overflow-x-clip bg-white px-[2rem] pt-[8rem] lg:px-[6rem] lg:pt-[12.5rem]">
      <div className="relative z-[1] mx-auto flex w-[min(171.25rem,100%)] flex-col items-center">
        <div className="relative flex w-full max-w-[68.25rem] flex-col items-center">
          {data.processChip && (
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.8, ease }}
              className="mb-0 text-center font-blacker text-[2rem] italic leading-[1.5] text-black"
            >
              {data.processChip}
            </motion.p>
          )}

          {data.processTitle && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.8, ease, delay: 0.1 }}
              className="richtext mt-[1.25rem] w-full prose-headings:mb-0 max-lg:prose-headings:text-[4.25rem] max-lg:prose-headings:leading-[0.94] lg:prose-headings:text-[6rem] lg:prose-headings:leading-[0.94]"
            >
              <PortableText value={data.processTitle} components={richTextComponent} />
            </motion.div>
          )}
        </div>

        {steps.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, ease, delay: 0.15 }}
            className="flex w-full flex-col gap-[1.5625rem] max-lg:mt-[4rem] lg:mt-[5rem] lg:flex-row lg:items-stretch"
          >
            {steps.map((step, index) => (
              <div key={step._key ?? index} className="w-full lg:flex-1">
                <StepCard step={step} index={index} />
              </div>
            ))}
          </motion.div>
        )}

        {processCTA?.cta && (
          <div className="mt-[4rem] w-full lg:w-[29.75rem]">
            <CTAButton
              data={{ ...processCTA, size: 'default', shape: 'square' }}
              overrideVariant="default"
              className="w-full [&_.button]:tracking-[-0.035rem] max-lg:[&_.button]:min-h-[6.75rem] max-lg:[&_.button]:text-[1.875rem] lg:[&_.button]:min-h-[5.875rem]"
            />
          </div>
        )}
      </div>
    </section>
  );
}
